import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { DevopsService } from '../../services/devops.service';
import { ReposFacade } from '../repos';
import { WorkspaceFacade } from '../workspace';
import { DevopsActions, DevopsOverviewSnapshot } from './devops.actions';
import { DEFAULT_DEVOPS_CAPABILITIES, devopsFeature } from './devops.reducer';

/**
 * Code Capsules DevOps integration. State lives in the `devops` NgRx feature;
 * this facade orchestrates the async HTTP work and dispatches the results.
 * Public surface (signals + async methods) mirrors the former signal store so
 * consumers are unchanged.
 */
@Injectable({ providedIn: 'root' })
export class DevopsFacade {
  private readonly store = inject(Store);
  private readonly svc = inject(DevopsService);
  private readonly repos = inject(ReposFacade);
  private readonly workspace = inject(WorkspaceFacade);

  private loadRequestVersion = 0;

  readonly loading = toSignal(this.store.select(devopsFeature.selectLoading), { initialValue: false });
  readonly busy = toSignal(this.store.select(devopsFeature.selectBusy), { initialValue: false });
  readonly error = toSignal(this.store.select(devopsFeature.selectError), { initialValue: null });
  readonly identity = toSignal(this.store.select(devopsFeature.selectIdentity), { initialValue: null });
  readonly platform = toSignal(this.store.select(devopsFeature.selectPlatform), { initialValue: null });
  readonly proxy = toSignal(this.store.select(devopsFeature.selectProxy), { initialValue: null });
  readonly capabilities = toSignal(this.store.select(devopsFeature.selectCapabilities), {
    initialValue: DEFAULT_DEVOPS_CAPABILITIES,
  });
  readonly teams = toSignal(this.store.select(devopsFeature.selectTeams), { initialValue: [] });
  readonly selectedTeamId = toSignal(this.store.select(devopsFeature.selectSelectedTeamId), { initialValue: null });
  readonly selectedTeam = toSignal(this.store.select(devopsFeature.selectSelectedTeam), { initialValue: null });
  readonly selectedSpaceId = toSignal(this.store.select(devopsFeature.selectSelectedSpaceId), { initialValue: null });
  readonly selectedCapsuleId = toSignal(this.store.select(devopsFeature.selectSelectedCapsuleId), {
    initialValue: null,
  });
  readonly selectedBuildId = toSignal(this.store.select(devopsFeature.selectSelectedBuildId), { initialValue: null });
  readonly spaces = toSignal(this.store.select(devopsFeature.selectSpaces), { initialValue: [] });
  readonly spaceGroups = toSignal(this.store.select(devopsFeature.selectSpaceGroups), { initialValue: [] });
  readonly capsules = toSignal(this.store.select(devopsFeature.selectCapsules), { initialValue: [] });
  readonly webDeployments = toSignal(this.store.select(devopsFeature.selectWebDeployments), { initialValue: [] });
  readonly builds = toSignal(this.store.select(devopsFeature.selectBuilds), { initialValue: [] });
  readonly buildLog = toSignal(this.store.select(devopsFeature.selectBuildLog), { initialValue: null });
  readonly incidents = toSignal(this.store.select(devopsFeature.selectIncidents), { initialValue: [] });
  readonly notes = toSignal(this.store.select(devopsFeature.selectNotes), { initialValue: [] });
  readonly projectName = toSignal(this.store.select(devopsFeature.selectProjectName), { initialValue: null });
  readonly projectRepoCount = toSignal(this.store.select(devopsFeature.selectProjectRepoCount), { initialValue: 0 });
  readonly matchedSpaceCount = toSignal(this.store.select(devopsFeature.selectMatchedSpaceCount), { initialValue: 0 });
  readonly matchedCapsuleCount = toSignal(this.store.select(devopsFeature.selectMatchedCapsuleCount), {
    initialValue: 0,
  });
  readonly showingFallbackSpaces = toSignal(this.store.select(devopsFeature.selectShowingFallbackSpaces), {
    initialValue: false,
  });
  readonly lastLoadedAt = toSignal(this.store.select(devopsFeature.selectLastLoadedAt), { initialValue: null });

  readonly isAuthenticated = computed(() => !!this.identity()?.authenticated);
  readonly hasPlatformAccess = computed(() => !!this.platform()?.authenticated);
  readonly hasActiveProxy = computed(() => {
    const status = String(this.proxy()?.status ?? '').toLowerCase();
    return status === 'starting' || status === 'running' || status === 'stopping';
  });

  async loadOverview(
    selection: { spaceId?: string | null; capsuleId?: string | null; buildId?: string | null } = {}
  ): Promise<void> {
    const requestVersion = ++this.loadRequestVersion;
    const activeProjectId = this.workspace.activeProjectId();
    const isStale = () =>
      requestVersion !== this.loadRequestVersion || this.workspace.activeProjectId() !== activeProjectId;
    this.store.dispatch(DevopsActions.loadStarted());
    try {
      const hasSpace = Object.prototype.hasOwnProperty.call(selection, 'spaceId');
      const hasCapsule = Object.prototype.hasOwnProperty.call(selection, 'capsuleId');
      const hasBuild = Object.prototype.hasOwnProperty.call(selection, 'buildId');
      const activeProject = this.workspace.activeProject();
      const projectName = String((activeProject as any)?.name ?? '').trim() || null;
      const projectRepos = (this.repos.settings()?.github?.repos ?? [])
        .map((repo) => {
          const owner = String(repo?.owner ?? '').trim();
          const name = String(repo?.name ?? '').trim();
          return owner && name ? `${owner}/${name}` : '';
        })
        .filter(Boolean);
      const data = await this.svc.overview({
        teamId: this.projectName() === projectName ? this.selectedTeamId() ?? undefined : undefined,
        spaceId: hasSpace ? selection.spaceId ?? undefined : this.selectedSpaceId() ?? undefined,
        capsuleId: hasCapsule ? selection.capsuleId ?? undefined : this.selectedCapsuleId() ?? undefined,
        buildId: hasBuild ? selection.buildId ?? undefined : this.selectedBuildId() ?? undefined,
        projectName,
        projectRepos,
      });
      if (isStale()) return;
      const snapshot: DevopsOverviewSnapshot = {
        identity: data.identity ?? null,
        platform: data.platform ?? null,
        proxy: data.proxy ?? null,
        capabilities: data.capabilities ?? DEFAULT_DEVOPS_CAPABILITIES,
        teams: Array.isArray(data.teams) ? data.teams : [],
        selectedTeamId: data.selectedTeamId ?? null,
        selectedTeam: data.selectedTeam ?? null,
        selectedSpaceId: data.selectedSpaceId ?? null,
        selectedCapsuleId: data.selectedCapsuleId ?? null,
        selectedBuildId: data.selectedBuildId ?? null,
        spaces: Array.isArray(data.spaces) ? data.spaces : [],
        spaceGroups: Array.isArray(data.spaceGroups) ? data.spaceGroups : [],
        capsules: Array.isArray(data.capsules) ? data.capsules : [],
        webDeployments: Array.isArray(data.webDeployments) ? data.webDeployments : [],
        builds: Array.isArray(data.builds) ? data.builds : [],
        buildLog: data.buildLog ?? null,
        incidents: Array.isArray(data.incidents) ? data.incidents : [],
        notes: Array.isArray(data.notes) ? data.notes.map((x: any) => String(x ?? '')) : [],
        projectName: data.projectName ?? projectName,
        projectRepoCount: Number(data.projectRepoCount ?? 0) || 0,
        matchedSpaceCount: Number(data.matchedSpaceCount ?? 0) || 0,
        matchedCapsuleCount: Number(data.matchedCapsuleCount ?? 0) || 0,
        showingFallbackSpaces: !!data.showingFallbackSpaces,
      };
      this.store.dispatch(DevopsActions.overviewLoaded({ snapshot }));
    } catch (e: any) {
      if (isStale()) return;
      this.store.dispatch(DevopsActions.loadFailed({ error: e?.message ?? 'Failed to load DevOps overview' }));
    }
  }

  async refreshWhoami(): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      this.store.dispatch(DevopsActions.identitySet({ identity: await this.svc.whoami() }));
    } catch (e: any) {
      this.store.dispatch(DevopsActions.failed({ error: e?.message ?? 'Failed to check Code Capsules session' }));
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      const result = await this.svc.login({ email, password });
      this.store.dispatch(DevopsActions.identitySet({ identity: result.identity ?? this.identity() }));
      this.store.dispatch(DevopsActions.platformSet({ platform: result.platform ?? this.platform() }));
      await this.loadOverview({ spaceId: null, capsuleId: null, buildId: null });
    } catch (e: any) {
      this.store.dispatch(DevopsActions.failed({ error: e?.message ?? 'Code Capsules login failed' }));
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async verifyTwoFactor(code: string): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      const result = await this.svc.verifyTwoFactor(code);
      this.store.dispatch(DevopsActions.platformSet({ platform: result.platform ?? this.platform() }));
      await this.loadOverview({ spaceId: null, capsuleId: null, buildId: null });
    } catch (e: any) {
      this.store.dispatch(
        DevopsActions.failed({ error: e?.message ?? 'Code Capsules authenticator code verification failed' })
      );
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async logout(): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      await this.svc.logout();
      this.store.dispatch(DevopsActions.loggedOut());
    } catch (e: any) {
      this.store.dispatch(DevopsActions.failed({ error: e?.message ?? 'Code Capsules logout failed' }));
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async startProxy(input: { spaceSlug: string; capsuleId: string; localPort: number }): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      const out = await this.svc.startProxy(input);
      this.store.dispatch(DevopsActions.proxySet({ proxy: out.proxy }));
    } catch (e: any) {
      this.store.dispatch(DevopsActions.failed({ error: e?.message ?? 'Failed to start proxy' }));
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async stopProxy(reason?: string): Promise<void> {
    this.store.dispatch(DevopsActions.busyStarted());
    try {
      const out = await this.svc.stopProxy(reason);
      this.store.dispatch(DevopsActions.proxySet({ proxy: out.proxy }));
    } catch (e: any) {
      this.store.dispatch(DevopsActions.failed({ error: e?.message ?? 'Failed to stop proxy' }));
    } finally {
      this.store.dispatch(DevopsActions.busyEnded());
    }
  }

  async pollProxyStatus(): Promise<void> {
    try {
      const out = await this.svc.proxyStatus();
      this.store.dispatch(DevopsActions.proxySet({ proxy: out.proxy }));
    } catch {
      // Quiet poll failure; explicit actions surface errors.
    }
  }

  async selectSpace(spaceId: string): Promise<void> {
    this.store.dispatch(
      DevopsActions.selectionPatched({
        selection: { selectedSpaceId: spaceId || null, selectedCapsuleId: null, selectedBuildId: null, buildLog: null },
      })
    );
    await this.loadOverview({ spaceId, capsuleId: null, buildId: null });
  }

  async selectCapsule(capsuleId: string): Promise<void> {
    const nextId = capsuleId || null;
    this.store.dispatch(
      DevopsActions.selectionPatched({
        selection: { selectedCapsuleId: nextId, selectedBuildId: null, buildLog: null, builds: [] },
      })
    );
    if (!nextId) return;
    await this.loadOverview({ capsuleId, buildId: null });
  }

  async selectBuild(buildId: string): Promise<void> {
    this.store.dispatch(
      DevopsActions.selectionPatched({ selection: { selectedBuildId: buildId || null, buildLog: null } })
    );
    await this.loadOverview({ buildId });
  }

  reset(): void {
    this.store.dispatch(DevopsActions.reset());
  }
}
